import { create } from 'zustand';
import { ILORPContactDetails } from '@src/components/lorp/lorp-contact-details/LORPContactDetailsStep';
import { getMaxBoxesForShipment } from '@src/utils';
import { DATE_TIME_ADJUSTMENT } from '@src/utils/calendar';
import { IReturnSession } from '../hooks/useReturnSession';
import { ItemToReturn, MilitaryExchange } from '../models/returns';
import {
  IPickupDate,
  FraudDecision,
  IPolicyValidatorResponse,
  IReturnEligibilityRequest,
  IReturnEligibilityV2Request,
  IReturnEligibilityResponse,
  IReturnMethodsResponse,
  IUpsGroupingRlcReturn,
  PolicyValidatorShipment,
} from '../models/api';
import {
  GetReturnsEligibilityDetails,
  GetReturnsEligibilityV2Details,
} from '../hooks/returns-hooks';

// Shipment object that includes additional states for each shipment such as box quantity and lorp checkbox state
export interface IReturnShipments extends PolicyValidatorShipment {
  shipmentId: number;
  // UPS only
  boxQuantity?: number;
  maxBoxes?: number;
  /**
   * Fewest boxes this group can be sent in, as the shipping service worked it
   * out. Distinct from maxBoxes in ownership as well as direction: the ceiling
   * is ours to compute, this floor is the service's to state. Undefined outside
   * the migration, where no floor above one exists.
   */
  minBoxes?: number;
  /**
   * The shipping group this leg was built from, when the shipping service is
   * what built it. Carried so the box count can be keyed back to its suite by
   * identity: matching on a shared line instead only holds while legs and
   * groups are one-to-one, which a review-step re-validate ends — it rebuilds
   * the drop-off legs from the validator's address split while the groups
   * still describe the service's weight split.
   */
  rlcReturnSuite?: string;
  // Populated after Create Return API call - each shipment gets the returnByDate from its returnCreate API response
  returnByDate?: string;
  // LORP only
  hasAgreed?: boolean; // used to track acknowledgement checkbox
  lorpContactDetails?: ILORPContactDetails;
  textOptIn?: boolean;
  pickupDate?: string;
  // SORIS only - populated after SORIS Create API call
  sorisId?: string;
  sorisQRCode?: string;
}
interface IReturnContext extends IReturnSession {
  returnEligibilityDetails?: IReturnEligibilityResponse;
  returnEligibilityLoading: boolean;
  policyValidatorResponse?: IPolicyValidatorResponse['returnService']['ssrHRTVResponse'];
  // Raw Return Methods response — narrowing happens in the response adapter.
  // Fetched at the quantity step so the answer exists before the method screen
  // renders; shipment legs are composed from it only once a method is selected.
  returnMethodsResponse?: IReturnMethodsResponse;
  // Which drop-off lines ship together, and the minimum boxes each group needs,
  // as the shipping service worked it out. Fetched once a method is chosen, and
  // rebuilt from each answer rather than merged into, so a group can never
  // outlive the response it came from.
  //
  // The shipment legs carry the grouping the screens read, so nothing consumes
  // this yet. It is kept because the suite naming each group survives nowhere
  // else, and the create request has to send the chosen box count keyed by that
  // suite once it takes an item list.
  upsGrouping?: IUpsGroupingRlcReturn[];
  selectedReturnItems: ItemToReturn[];
  orderBarcode?: string;
  receiptNumber?: string;
  lorpContactDetails?: ILORPContactDetails;
  isMultiAddressReturn?: boolean;
  returnShipments?: IReturnShipments[];
  returnBoxes: number;
  multipleReturnBoxes: number[];
  shipmentGroupedLineItems?: ItemToReturn[][];
  selectedItems: ItemToReturn[];
  fraudDecisions: FraudDecision[];
  rosTransactionId?: string;
  pickupDates: IPickupDate[];
  fetchScheduledPickups: boolean;
  showCalendar: boolean;
}

const initialState = {
  orderDetailsV2: undefined,
  orderId: undefined,
  isMilitaryExchangeGuest: false,
  tenders: [],
  returnEligibilityDetails: undefined,
  returnEligibilityLoading: false,
  policyValidatorResponse: undefined,
  returnMethodsResponse: undefined,
  upsGrouping: undefined,
  selectedReturnItems: [],
  orderBarcode: '',
  receiptNumber: '',
  militaryExchange: '' as MilitaryExchange,
  lorpContactDetails: undefined,
  isMultiAddressReturn: false,
  returnShipments: undefined,
  returnBoxes: 1,
  multipleReturnBoxes: [],
  shipmentGroupedLineItems: undefined,
  orderDetailsPath: '/myaccount/order-details',
  selectedItems: [],
  ptcNickname: undefined,
  isPTCOrder: undefined,
  isApoFpo: undefined,
  fraudDecisions: [],
  rosTransactionId: undefined,
  pickupDates: [],
  fetchScheduledPickups: false,
  showCalendar: false,
};

export const useReturnStore = create<IReturnContext>()(() => ({
  ...initialState,
}));

export const setReturnSession = ({
  orderDetailsV2,
  tenders,
  orderId,
  isMilitaryExchangeGuest,
  militaryExchange,
  orderBarcode,
  receiptNumber,
  orderDetailsPath = '/myaccount/order-details',
  ptcNickname,
  isPTCOrder,
  isApoFpo,
}: IReturnSession) =>
  useReturnStore.setState({
    orderDetailsV2,
    tenders,
    orderId,
    isMilitaryExchangeGuest,
    militaryExchange,
    orderBarcode,
    receiptNumber,
    orderDetailsPath,
    ptcNickname,
    isPTCOrder,
    isApoFpo,
  });

export const setReturnEligibilityLoading = (value: boolean) =>
  useReturnStore.setState({ returnEligibilityLoading: value });

// TODO we can remove returnBoxes once multipleReturnBoxes is fully implemented
export const setReturnBoxes = (value: number) =>
  useReturnStore.setState({ returnBoxes: value });

export const setMultipleReturnBoxes = (value: number[]) =>
  useReturnStore.setState({ multipleReturnBoxes: value });

export const setReturnEligibilityDetails = (
  value: IReturnEligibilityResponse,
) => useReturnStore.setState({ returnEligibilityDetails: value });

/**
 * Replaces the grouping wholesale. Passing undefined clears it, which is what a
 * failed or abandoned attempt must do — a stale grouping left in place would
 * describe a return the customer is no longer making.
 */
export const setUpsGrouping = (value?: IUpsGroupingRlcReturn[]) =>
  useReturnStore.setState({ upsGrouping: value });

export const setSelectedReturnItems = (value: ItemToReturn[]) =>
  useReturnStore.setState({ selectedReturnItems: value });

export const setSelectedItems = (value: ItemToReturn[]) =>
  useReturnStore.setState({ selectedItems: value });

export const setUpdatedItem = (updatedItem: ItemToReturn) =>
  useReturnStore.setState((state) => ({
    selectedItems: state.selectedItems.map((item) =>
      item.lineItemId === updatedItem.lineItemId ? updatedItem : item,
    ),
  }));

export const setUpdatedShipmentFields = (
  shipmentIds: number[],
  fields: Partial<IReturnShipments>,
) => {
  useReturnStore.setState((state) => ({
    returnShipments: state.returnShipments?.map((shipment) =>
      shipmentIds.includes(shipment.shipmentId)
        ? { ...shipment, ...fields }
        : shipment,
    ),
  }));
};

export const setIsMultiAddressReturn = (value: boolean) =>
  useReturnStore.setState({ isMultiAddressReturn: value });

export const setReturnShipments = (value: IReturnShipments[]) =>
  useReturnStore.setState({ returnShipments: value });

export const deleteItemFromShipment = (itemIdToRemove: string) => {
  useReturnStore.setState((state) => {
    const newShipments = state.returnShipments?.map((shipment) => {
      if (
        !shipment.lineItems.some((item) => item.lineItemId === itemIdToRemove)
      ) {
        return shipment;
      }
      const updatedLineItems = shipment.lineItems.filter(
        (item) => item.lineItemId !== itemIdToRemove,
      );
      const modifiedShipment = { ...shipment, lineItems: updatedLineItems };

      return modifiedShipment;
    });
    const filteredShipments = newShipments?.filter(
      (shipment) => shipment.lineItems.length > 0,
    );
    return {
      returnShipments: filteredShipments,
    };
  });
};

export const updateMaxBoxesShipments = (modifiedItems: ItemToReturn[]) => {
  useReturnStore.setState((state) => ({
    returnShipments: state.returnShipments?.map((shipment) => {
      if (shipment.shipmentType !== 'DROPOFF') {
        return shipment;
      }
      const maxBoxes = getMaxBoxesForShipment(
        shipment,
        modifiedItems,
        state.orderDetailsV2,
      );

      // Removing an item lowers the ceiling, which can leave a count the
      // customer chose under the old one stranded above the new one. Only the
      // ceiling was recomputed here before, so that count survived unchallenged
      // and travelled to the create call. The floor holds it from below for the
      // same reason the picker does — a lower count is silently overridden at
      // submit — so it wins if the two ever cross.
      const minBoxes = shipment.minBoxes || 1;
      const boxQuantity =
        shipment.boxQuantity === undefined
          ? shipment.boxQuantity
          : Math.max(Math.min(shipment.boxQuantity, maxBoxes), minBoxes);

      return {
        ...shipment,
        maxBoxes,
        boxQuantity,
      };
    }),
  }));
};

export const setShipmentGroupedLineItems = (value: ItemToReturn[][]) =>
  useReturnStore.setState({ shipmentGroupedLineItems: value });

export const getReturnEligibilityDetails = (
  requestData: IReturnEligibilityRequest,
) => {
  setReturnEligibilityLoading(true);
  return GetReturnsEligibilityDetails(
    requestData,
    setReturnEligibilityDetails,
  ).finally(() => setReturnEligibilityLoading(false));
};

export const getReturnEligibilityV2Details = (
  requestData: IReturnEligibilityV2Request,
  net: string,
) => {
  setReturnEligibilityLoading(true);
  return GetReturnsEligibilityV2Details(
    requestData,
    net,
    setReturnEligibilityDetails,
  ).finally(() => setReturnEligibilityLoading(false));
};

export const setPolicyValidatorResponse = (
  response?: IPolicyValidatorResponse['returnService']['ssrHRTVResponse'],
) => useReturnStore.setState({ policyValidatorResponse: response });

/**
 * Accepts undefined so the answer can be put back exactly as it was found —
 * including having been absent. A restore that could only write a value would
 * leave a freshly issued answer standing for a change that was abandoned.
 */
export const setReturnMethodsResponse = (response?: IReturnMethodsResponse) =>
  useReturnStore.setState({ returnMethodsResponse: response });

export const setLorpContactDetails = (value: ILORPContactDetails) =>
  useReturnStore.setState({ lorpContactDetails: value });

export const _mockReturnStore = (mockedState: Partial<IReturnContext>) =>
  useReturnStore.setState((state) => ({ ...state, ...mockedState }));

export const _resetReturnStore = () =>
  useReturnStore.setState(initialState, true);

/**
 * The identifier ROS supplied for the fraud check, held from the Return Methods
 * response through to the Appriss report. Written by the methods call, read only
 * by the report builder. It exists because ROS answers with one order-level id
 * rather than one per leg, leaving `getIdentifier` nothing to select from.
 */
export const setRosTransactionId = (rosTransactionId?: string) => {
  useReturnStore.setState((state) => ({ ...state, rosTransactionId }));
};

export const setFraudDecisions = (fraudDecisions: FraudDecision[]) => {
  useReturnStore.setState((state) => ({ ...state, fraudDecisions }));
};

export const setPickupDates = (value: IPickupDate[]) => {
  // Modifies YYYY-MM-DD into DateTime ISO 8601 for date calculations
  const pickupDates = value.map((slot) => ({
    ...slot,
    date: slot.date.includes('T')
      ? slot.date
      : `${slot.date}${DATE_TIME_ADJUSTMENT}`,
  }));

  useReturnStore.setState({ pickupDates });
};

export const setFetchScheduledPickups = (value: boolean) =>
  useReturnStore.setState({ fetchScheduledPickups: value });

export const setShowCalendar = (value: boolean) =>
  useReturnStore.setState({ showCalendar: value });
